import React from "react";
import { Plus, Trash2, User, Users, Sparkles, ClipboardCheck, ChevronRight, ChevronLeft } from "lucide-react";
import { usd, itemKey, personKey } from "../../lib/api";
import ServicesEditor from "../ServicesEditor";

export const STEPS = ["Participants & Kit", "Optional Services", "Guardian & Consent", "Review"];
const GENDERS = ["Male", "Female", "Prefer not to say"];
export const inputCls = "w-full rounded-xl border-2 border-[#E2EBF0] px-3 py-2.5 text-sm focus:border-[#008057] outline-none bg-white";
const card = "rounded-2xl bg-white border border-[#E2EBF0] p-6 shadow-lg shadow-emerald-950/5";

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-[#03281B]">{label}{required && <span className="text-red-500"> *</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

const Select = ({ testId, value, options, onChange }) => (
  <select data-testid={testId} className={inputCls} value={value} onChange={(e) => onChange(e.target.value)}>
    <option value="">Select</option>{options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export const ProgressBar = ({ step }) => (
  <div data-testid="registration-progress-bar" className="mb-8">
    <div className="flex items-center gap-2">
      {STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`flex items-center gap-2 ${i <= step ? "text-[#008057]" : "text-slate-400"}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${i <= step ? "bg-[#008057] text-white" : "bg-slate-200 text-slate-500"}`}>{i + 1}</div>
            <span className="hidden sm:inline text-sm font-semibold">{s}</span>
          </div>
          {i < STEPS.length - 1 && <div className={`flex-1 h-1 rounded-full ${i < step ? "bg-[#008057]" : "bg-slate-200"}`} />}
        </React.Fragment>
      ))}
    </div>
  </div>
);

const KitSizes = ({ p, i, sizes, setP }) => (
  <div className="mt-5">
    <div className="text-xs font-semibold uppercase tracking-widest text-[#008057] mb-3">Training Kit Sizes</div>
    <div className="grid sm:grid-cols-3 gap-4">
      <Field label="T-Shirt size" required><Select testId={`select-kit-size-${i}`} value={p.tshirt_size} options={sizes.tshirt} onChange={(v) => setP(i, { tshirt_size: v })} /></Field>
      <Field label="Shorts size" required><Select value={p.shorts_size} options={sizes.shorts} onChange={(v) => setP(i, { shorts_size: v })} /></Field>
      <Field label="Socks size" required><Select value={p.socks_size} options={sizes.socks} onChange={(v) => setP(i, { socks_size: v })} /></Field>
    </div>
  </div>
);

const Medical = ({ p, i, setP }) => (
  <div className="mt-5">
    <div className="text-xs font-semibold uppercase tracking-widest text-[#008057] mb-2">Medical / Allergies</div>
    <label className="flex items-center gap-2 text-sm font-semibold text-[#03281B]">
      <input type="checkbox" className="accent-[#008057] w-4 h-4" checked={p.medical_none} onChange={(e) => setP(i, { medical_none: e.target.checked, medical_details: e.target.checked ? "" : p.medical_details })} />
      None
    </label>
    {!p.medical_none && <textarea className={`${inputCls} mt-2`} rows={2} placeholder="Injuries, medical considerations, or allergies" value={p.medical_details} onChange={(e) => setP(i, { medical_details: e.target.value })} />}
  </div>
);

const ParticipantCard = ({ p, i, canRemove, sizes, setP, onRemove }) => (
  <div className={card}>
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2 font-display font-bold text-lg text-[#03281B]"><User size={20} className="text-[#008057]" /> Participant {i + 1}</div>
      {canRemove && <button data-testid={`remove-participant-${i}`} onClick={() => onRemove(i)} className="text-red-500 hover:text-red-700 flex items-center gap-1 text-sm font-semibold"><Trash2 size={16} /> Remove</button>}
    </div>
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="First name" required><input data-testid={`input-participant-name-${i}`} className={inputCls} value={p.first_name} onChange={(e) => setP(i, { first_name: e.target.value })} /></Field>
      <Field label="Last name" required><input className={inputCls} value={p.last_name} onChange={(e) => setP(i, { last_name: e.target.value })} /></Field>
      <Field label="Date of birth" required><input data-testid={`input-participant-dob-${i}`} type="date" className={inputCls} value={p.dob} onChange={(e) => setP(i, { dob: e.target.value })} /></Field>
      <Field label="Gender" required><Select value={p.gender} options={GENDERS} onChange={(v) => setP(i, { gender: v })} /></Field>
    </div>
    <KitSizes p={p} i={i} sizes={sizes} setP={setP} />
    <Medical p={p} i={i} setP={setP} />
  </div>
);

export const ParticipantsStep = ({ participants, sizes, setP, onAdd, onRemove }) => (
  <div data-testid="step-1-participant-form" className="space-y-6">
    {participants.map((p, i) => <ParticipantCard key={p._k} p={p} i={i} canRemove={participants.length > 1} sizes={sizes} setP={setP} onRemove={onRemove} />)}
    <button data-testid="add-participant-btn" onClick={onAdd} className="w-full rounded-2xl border-2 border-dashed border-[#008057]/40 text-[#008057] font-bold py-4 flex items-center justify-center gap-2 hover:bg-[#F2F8F5] transition-colors">
      <Plus size={20} /> Add Another Participant
    </button>
  </div>
);

export const ServicesStep = ({ participants, updateParticipant }) => (
  <div data-testid="step-3-optional-services" className="space-y-6">
    {participants.map((p, i) => (
      <div key={p._k} className={card}>
        <div className="flex items-center gap-2 font-display font-bold text-lg text-[#03281B] mb-5"><Sparkles size={20} className="text-[#F3C242]" /> {p.first_name || `Participant ${i + 1}`} &mdash; Optional Services</div>
        <ServicesEditor participant={p} onChange={(np) => updateParticipant(i, np)} />
      </div>
    ))}
  </div>
);

const CONSENTS = [
  ["guardian_confirm", "I confirm I am the participant's parent or authorized legal guardian."],
  ["terms", "I accept the camp's registration terms and conditions."],
  ["privacy", "I acknowledge the privacy policy and processing of personal information necessary for registration."],
  ["medical_share", "I acknowledge that relevant injury, allergy, and medical information may be shared with authorized camp staff for participant safety."],
];

const GuardianFields = ({ guardian, setG }) => (
  <div className={card}>
    <div className="flex items-center gap-2 font-display font-bold text-lg text-[#03281B] mb-4"><Users size={20} className="text-[#008057]" /> Parent / Legal Guardian</div>
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="First name" required><input data-testid="input-guardian-name" className={inputCls} value={guardian.first_name} onChange={(e) => setG({ first_name: e.target.value })} /></Field>
      <Field label="Last name" required><input className={inputCls} value={guardian.last_name} onChange={(e) => setG({ last_name: e.target.value })} /></Field>
      <Field label="Email address" required><input data-testid="input-guardian-email" type="email" className={inputCls} value={guardian.email} onChange={(e) => setG({ email: e.target.value })} /></Field>
      <Field label="Mobile phone" required><input data-testid="input-guardian-phone" className={inputCls} value={guardian.phone} onChange={(e) => setG({ phone: e.target.value })} placeholder="+1 555 123 4567" /></Field>
      <Field label="Country of residence"><input className={inputCls} value={guardian.country} onChange={(e) => setG({ country: e.target.value })} /></Field>
    </div>
  </div>
);

const GiftRecipient = ({ participants, value, onChange }) => (
  <div className="rounded-2xl bg-amber-50 border-2 border-[#F3C242] p-6">
    <div className="font-bold text-[#03281B] mb-2">Jersey Gift Recipient</div>
    <p className="text-sm text-slate-600 mb-3">If your registration qualifies for the retro Cristiano Ronaldo jersey (first 5, confirmed payment), which participant should receive it?</p>
    <select data-testid="gift-recipient-select" className={inputCls} value={value} onChange={(e) => onChange(Number(e.target.value))}>
      {participants.map((p, i) => <option key={p._k} value={i}>{p.first_name} {p.last_name}</option>)}
    </select>
  </div>
);

const ConsentList = ({ consents, setConsents, guardian, setG }) => (
  <div className={card}>
    <div className="font-display font-bold text-lg text-[#03281B] mb-4">Consent & Privacy</div>
    <div className="space-y-3">
      {CONSENTS.map(([k, label]) => (
        <label key={k} data-testid={`consent-${k}`} className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" className="accent-[#008057] w-5 h-5 mt-0.5" checked={consents[k]} onChange={(e) => setConsents({ ...consents, [k]: e.target.checked })} />
          <span className="text-sm text-slate-700">{label} <span className="text-red-500">*</span></span>
        </label>
      ))}
      <label className="flex items-start gap-3 cursor-pointer border-t border-slate-100 pt-3">
        <input type="checkbox" className="accent-[#008057] w-5 h-5 mt-0.5" checked={guardian.marketing_consent} onChange={(e) => setG({ marketing_consent: e.target.checked })} />
        <span className="text-sm text-slate-700">I would like to receive occasional marketing updates (optional).</span>
      </label>
    </div>
  </div>
);

export const GuardianStep = ({ guardian, setGuardian, consents, setConsents, participants, showGift, giftRecipientIndex, setGiftRecipientIndex }) => {
  const setG = (patch) => setGuardian({ ...guardian, ...patch });
  return (
    <div data-testid="step-2-guardian-form" className="space-y-6">
      <GuardianFields guardian={guardian} setG={setG} />
      {showGift && <GiftRecipient participants={participants} value={giftRecipientIndex} onChange={setGiftRecipientIndex} />}
      <ConsentList consents={consents} setConsents={setConsents} guardian={guardian} setG={setG} />
    </div>
  );
};

export const ReviewStep = ({ quote, guardian }) => (
  <div className="space-y-6">
    <div className={card}>
      <div className="flex items-center gap-2 font-display font-bold text-lg text-[#03281B] mb-4"><ClipboardCheck size={20} className="text-[#008057]" /> Review Your Registration</div>
      <div className="text-sm text-slate-600 mb-4">Category: <strong className="text-[#03281B]">{quote?.category?.name}</strong> &middot; {usd(quote?.category?.price)} per participant</div>
      {quote?.participants?.map((p) => (
        <div key={personKey(p)} className="border-t border-slate-100 py-3">
          <div className="font-semibold text-[#03281B]">{p.name}</div>
          {p.items.map((it) => (
            <div key={itemKey(it)} className="flex justify-between text-sm text-slate-600"><span>{it.label} <span className="text-slate-400">- {it.detail}</span></span><span className="font-mono">{usd(it.amount)}</span></div>
          ))}
          <div className="flex justify-between text-sm font-semibold mt-1"><span>Subtotal</span><span>{usd(p.subtotal)}</span></div>
        </div>
      ))}
      <div className="flex justify-between font-display font-extrabold text-xl text-[#03281B] border-t-2 border-[#008057] pt-3 mt-2"><span>Total</span><span>{usd(quote?.total)}</span></div>
    </div>
    <p className="text-sm text-slate-500">Guardian: {guardian.first_name} {guardian.last_name} &middot; {guardian.email} &middot; {guardian.phone}</p>
  </div>
);

export const WizardNav = ({ step, onBack, onNext, onCheckout }) => (
  <div className="flex items-center justify-between pt-2">
    {step > 0
      ? <button data-testid="wizard-back" onClick={onBack} className="rounded-full border-2 border-[#E2EBF0] px-6 py-2.5 font-bold text-slate-600 flex items-center gap-1 hover:border-[#008057]"><ChevronLeft size={18} /> Back</button>
      : <span />}
    {step < STEPS.length - 1
      ? <button data-testid="wizard-next" onClick={onNext} className="rounded-full bg-[#008057] hover:bg-[#054D34] text-white px-8 py-2.5 font-bold flex items-center gap-1 transition-colors">Continue <ChevronRight size={18} /></button>
      : <button data-testid="proceed-to-checkout" onClick={onCheckout} className="rounded-full bg-[#F3C242] hover:brightness-105 text-[#03281B] px-8 py-2.5 font-bold flex items-center gap-1 transition-all gold-glow">Proceed to Checkout <ChevronRight size={18} /></button>}
  </div>
);
